import React, { useState, useEffect } from 'react';
import { bool, func, object, number, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, intlShape } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { propTypes } from '../../../../util/types';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  IconHelp,
  IconEnvelope,
  IconCart,
  IconStationary,
  IconAccountEdit,
  IconTune,
  IconLogout,
  IconPackageBox,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';
import CategoriesMenu from './CategoriesMenu/CategoriesMenu';

import css from './TopbarDesktop.module.css';
import { IconHeartOutlined } from '../../../../components/IconHeart/IconHeartOutlined';

const NotificationBadge = ({ count }) => {
  if (!count) {
    return null;
  }

  return <div className={css.notificationBadge}>{count}</div>;
};

const NotificationDot = ({ count }) => {
  if (!count) {
    return null;
  }

  return <div className={css.notificationDot} />;
};

export const LoginLink = () => {
  return (
    <NamedLink name="LoginPage" className={css.topbarLoginLink}>
      <FormattedMessage id="TopbarDesktop.login" />
    </NamedLink>
  );
};

export const CreateListingLink = () => {
  return (
    <NamedLink name="NewListingPage" className={css.topbarCreateListingLink}>
      <FormattedMessage id="TopbarDesktop.createListing" />
    </NamedLink>
  );
};

export const HelpLink = () => {
  return (
    <NamedLink name="FAQPage" className={css.topbarLink}>
      <span className={css.topbarLinkIcon}>
        <IconHelp />
      </span>
    </NamedLink>
  );
};

export const InboxLink = ({ newMessagesCount }) => {
  return (
    <NamedLink className={css.topbarLink} name="ConversationsPage">
      <span className={css.topbarLinkIcon}>
        <IconEnvelope />
        <NotificationBadge count={newMessagesCount} />
      </span>
    </NamedLink>
  );
};

export const WishlistLink = ({ wishlistCount }) => {
  return (
    <NamedLink className={css.topbarLink} name="WishlistPage">
      <span className={css.topbarLinkIcon}>
        <IconHeartOutlined />
        <NotificationBadge count={wishlistCount} />
      </span>
    </NamedLink>
  );
};

export const CartLink = ({ cartCount }) => {
  return (
    <NamedLink className={css.topbarLink} name="CartPage">
      <span className={css.topbarLinkIcon}>
        <IconCart />
        <NotificationBadge count={cartCount} />
      </span>
    </NamedLink>
  );
};

const ProfileMenu = ({ currentPage, currentUser, notificationCount, onLogout }) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  return (
    <Menu useArrow={false} contentPosition="center" contentPlacementOffset="50%">
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
        <NotificationDot count={notificationCount} />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent} arrowPosition={null}>
        <MenuItem key="ConversationsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('InboxPage'))}
            name="ConversationsPage"
          >
            <span className={css.menuItemBorder} />
            <IconEnvelope className={css.menuIcon} />
            <FormattedMessage id="TopbarDesktop.inboxLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="InboxPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('InboxPage'))}
            name="InboxPage"
            params={{ tab: 'orders' }}
          >
            <span className={css.menuItemBorder} />
            <IconPackageBox className={css.menuIcon} />
            <FormattedMessage id="TopbarDesktop.transactionBoxLink" />
            <NotificationBadge count={notificationCount} />
          </NamedLink>
        </MenuItem>
        <MenuItem key="ManageListingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
            name="ManageListingsPage"
          >
            <span className={css.menuItemBorder} />
            <IconStationary className={css.menuIcon} />
            <FormattedMessage id="TopbarDesktop.listingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <IconAccountEdit className={css.menuIcon} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <IconTune className={css.menuIcon} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <IconLogout className={css.menuIcon} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    notificationCount,
    newMessagesCount,
    cartCount,
    wishlistCount,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues,
  } = props;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);

  return (
    <nav className={classes}>
      <LinkedLogo
        className={css.logoLink}
        layout="desktop"
        alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
        linkToExternalSite={config?.topbar?.logoLink}
      />
      <TopbarSearchForm
        className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
        desktopInputRoot={css.topbarSearch}
        onSubmit={onSearchSubmit}
        initialValues={initialSearchFormValues}
        appConfig={config}
      />
      <CustomLinksMenu
        currentPage={currentPage}
        customLinks={customLinks}
        intl={intl}
        hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
      />
      {authenticatedOnClientSide && (
        <>
          <InboxLink newMessagesCount={newMessagesCount} />
          <WishlistLink wishlistCount={wishlistCount} />
          <CartLink cartCount={cartCount} />
          <ProfileMenu
            currentPage={currentPage}
            currentUser={currentUser}
            onLogout={onLogout}
            currentUserHasListings={currentUserHasListings}
            notificationCount={notificationCount}
          />
        </>
      )}
      {!isAuthenticatedOrJustHydrated && <LoginLink />}
      <CreateListingLink />
      <HelpLink />
      <CategoriesMenu config={config} layout="desktop" />
    </nav>
  );
};

TopbarDesktop.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  currentPage: null,
  notificationCount: 0,
  initialSearchFormValues: {},
  config: null,
};

TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  isAuthenticated: bool.isRequired,
  onLogout: func.isRequired,
  notificationCount: number,
  onSearchSubmit: func.isRequired,
  initialSearchFormValues: object,
  intl: intlShape.isRequired,
  config: object,
};

export default TopbarDesktop;
